import { forwardRef } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "cursor-pointer inline-flex items-center justify-center rounded-sm text-sm font-medium ring-offset-white transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-30",
  {
    variants: {
      variant: {
        primary:
          "bg-btn-bg-primary text-btn-fg-primary hover:bg-btn-bg-primary/90",
        secondary:
          "bg-btn-bg-secondary text-btn-fg-secondary hover:bg-btn-bg-secondary/70",
        destructive:
          "bg-btn-bg-destructive text-btn-fg-destructive hover:bg-btn-bg-destructive/90",
        caution:
          "bg-btn-bg-caution text-btn-fg-caution hover:bg-btn-bg-caution/90",
        accentWarm:
          "bg-accent-bg-warm text-white/90 hover:bg-accent-bg-warm/90",
        accentHot: "bg-accent-bg-hot text-white/90 hover:bg-accent-bg-hot/90",
        outline:
          "border border-btn-bd-outline text-btn-fg-outline hover:bg-btn-bd-outline/5",
        ghost: "hover:bg-slate-100 hover:text-slate-900",
        link: "text-btn-fg-link underline underline-offset-4 hover:underline-offset-5",
        tab: "rounded-none",
      },
      size: {
        default: "h-10 px-4 py-3",
        xs: "h-6 px-1 py-1",
        sm: "h-9 rounded-md px-3",
        lg: "h-12 rounded-md px-8",
        xl: "h-16 rounded-md text-lg font-semibold px-12",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, ...props }, ref) => {
    return (
      <button
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);

Button.displayName = "Button";

export { Button, buttonVariants };
